import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaPlay, FaBook, FaGraduationCap, FaStar, FaClock, FaUsers, FaQuestionCircle } from 'react-icons/fa';
import { persianQuestionsData } from '../data/persianQuestions';
import type { Lesson } from '../data/persianQuestions';
import { useAuth } from '../contexts/AuthContext';

const VajakLessons: React.FC = () => {
  const [completedLessons, setCompletedLessons] = useState<number[]>([]);
  const [totalProgress, setTotalProgress] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    // Load progress from localStorage
    const savedProgress = localStorage.getItem('vajak-progress');
    if (savedProgress) {
      const progress = JSON.parse(savedProgress);
      setCompletedLessons(progress.completedLessons || []);
      setTotalProgress(progress.totalProgress || 0);
    }
  }, []);

  const getLessonProgress = (lessonId: number): number => {
    return completedLessons.includes(lessonId) ? 100 : 0;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'advanced': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'Beginner';
      case 'intermediate': return 'Intermediate';
      case 'advanced': return 'Advanced';
      default: return difficulty;
    }
  };

  const isPremium = user && (user.subscription.type === 'premium' || user.subscription.type === 'lifetime');

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <FaQuestionCircle className="text-4xl text-orange-500 mr-3" />
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white drop-shadow-lg">
              Pirooz Lessons
            </h1>
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
          پیروز - Persian Conversation Lessons
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Practice real-life Persian with interactive lessons, quizzes, and flashcards. Track your progress and master everyday conversation!
          </p>
        </div>

        {/* Progress Overview */}
        <div className="bg-white/95 dark:bg-gray-900/95 rounded-2xl p-6 shadow-lg mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              Overall Progress
            </h3>
            <span className="text-2xl font-bold text-orange-500">
              {Math.round(totalProgress)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-orange-500 to-red-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${totalProgress}%` }}
            ></div>
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-700 dark:text-gray-300">
            <span>{completedLessons.length} of {persianQuestionsData.length} lessons completed</span>
            <span>{Math.round((completedLessons.length / persianQuestionsData.length) * 100)}% complete</span>
          </div>
        </div>

        {/* Lessons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {persianQuestionsData.map((lesson: Lesson, idx: number) => {
            const progress = getLessonProgress(typeof lesson.id === 'number' ? lesson.id : Number(lesson.id));
            const isCompleted = progress === 100;
            const firstQuestion = lesson.questions[0];
            const isLocked = idx >= persianQuestionsData.length - 3 && !isPremium;
            return (
              <div 
                key={lesson.id}
                className={`bg-white/95 dark:bg-gray-900/95 rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                  isCompleted ? 'ring-2 ring-green-500' : ''
                } ${isLocked ? 'opacity-70' : ''}`}
              >
                {/* Lesson Header */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <span className="text-3xl mr-3">{lesson.icon}</span>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                          {lesson.title}
                        </h3>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {lesson.persianTitle}
                        </p>
                      </div>
                    </div>
                    {isCompleted && (
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <FaStar className="text-white text-sm" />
                      </div>
                    )}
                    {isLocked && (
                      <div className="w-8 h-8 bg-orange-200 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm6-10V7a4 4 0 10-8 0v2" /></svg>
                      </div>
                    )}
                  </div>
                  <p className="text-base text-gray-800 dark:text-gray-200 mb-4 leading-relaxed">
                    {lesson.description}
                  </p>
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
                    <div 
                      className="bg-gradient-to-r from-orange-500 to-red-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                    <span>{progress}% complete</span>
                    <span>{lesson.questions.length} questions</span>
                  </div>
                </div>
                {/* Lesson Details */}
                <div className="p-6">
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <FaBook className="mr-2 text-orange-500" />
                      <span><strong>Focus:</strong> {lesson.focus}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <FaGraduationCap className="mr-2 text-orange-500" />
                      <span><strong>Goal:</strong> {lesson.goal}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <FaClock className="mr-2 text-orange-500" />
                      <span><strong>Estimated time:</strong> 20-30 minutes</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <FaUsers className="mr-2 text-orange-500" />
                      <span><strong>Level:</strong> {firstQuestion?.difficulty ? getDifficultyText(firstQuestion.difficulty) : 'Beginner'}</span>
                    </div>
                  </div>
                  {/* Questions Preview */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                      Sample questions:
                    </h4>
                    <div className="space-y-2">
                      {lesson.questions.slice(0, 2).map((q) => (
                        <div key={q.id} className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {q.persian}
                            </p>
                            <p className="text-xs text-gray-700 dark:text-gray-300">
                              {q.english}
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(q.difficulty)}`}>
                            {getDifficultyText(q.difficulty)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Action Buttons */}
                  <div className="space-y-3">
                    {isLocked ? (
                      <button
                        className="w-full flex items-center justify-center px-4 py-3 rounded-lg font-semibold bg-gray-300 text-gray-500 cursor-not-allowed"
                        onClick={() => alert('This lesson is for Premium & Platinum members only. Please upgrade to access.')}
                        disabled
                      >
                        <svg className="w-5 h-5 mr-2 text-orange-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm6-10V7a4 4 0 10-8 0v2" /></svg>
                        Premium & Platinum Only
                      </button>
                    ) : (
                    <Link
                      to={`/vajak/lesson/${lesson.id}`}
                      className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-semibold transition-all duration-300 ${
                        isCompleted
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white'
                      }`}
                    >
                      <FaPlay className="mr-2" />
                      {isCompleted ? 'Review Lesson' : 'Start Lesson'}
                    </Link>
                    )}
                    <Link
                      to={`/vajak/flashcards/${lesson.id}`}
                      className="w-full flex items-center justify-center px-4 py-2 border-2 border-orange-500 text-orange-500 dark:text-orange-400 rounded-lg font-semibold hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-300"
                    >
                      <FaBook className="mr-2" />
                      Practice Flashcards
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default VajakLessons; 